import { useTranslator, useDateFormatter } from '@bluebeam/react-i18n';
import classnames from 'classnames';
import { OrderedMap } from 'immutable';
import Calendar from 'components/Icons/Calendar';
import Profile from 'components/Icons/Profile';
import { useRFIManager } from 'providers/RFIManagerProvider';
import React, { ReactElement, useEffect, useState } from 'react';
import { Markup } from 'records/Markup';
import { Project } from 'records/Project';
import { RFI } from 'records/RFI';
import RfiVersionListDropdown from 'components/Overlays/RFIOverlays/ViewRFIOverlay/RfiVersionListDropdown';

interface Props {
  rfi: RFI;
  markup: Markup;
  project: Project;
  onRFIListRowClick: (rfi: RFI, markup: Markup) => void;
}

const RFIListRow = (props: Props): ReactElement => {
  const { rfi, markup, onRFIListRowClick } = props;
  const { number, versionnumber, duedate, title, assignees, answer } = rfi;
  const [rfiVersionList, setRfiVersionList] = useState<OrderedMap<string, RFI>>(
    OrderedMap([])
  );
  const { translate } = useTranslator(['mapView']);
  const dateFormatter = useDateFormatter({
    dateStyle: 'medium',
  });

  const { getRFI, getRFIVersionsByBaseId } = useRFIManager();

  useEffect(() => {
    const [baseId] = rfi.id.split('+');
    setRfiVersionList(getRFIVersionsByBaseId(baseId));
  }, [rfi.id]);

  const assigneeList = [] as string[];
  assignees.forEach((a) => assigneeList.push(a.email));

  const pastDueDate =
    answer === null && duedate !== 0 && duedate < Date.now() / 1000;

  const isOpen =
    answer === null && rfi.nextversionid === '' && rfi.previousversionid !== '';

  const hasDueDate = rfi.duedate !== 0;

  return (
    <div
      className="bb-2 color-gray-7 bgd-gray-2-h cursor-pointer py3 px4"
      id={`rfi-list-item-${rfi.id}`}
      onClick={(): void => onRFIListRowClick(rfi, markup)}
      data-testid="rfi-list-item"
    >
      <div className="column">
        <div className="row justify-between">
          <div
            className={classnames(
              'center bgd-light-grey-1 py1 px3 b-radius-3 black-2 bold',
              {
                'bgd-status-red': isOpen,
              }
            )}
          >
            {translate('mapView:postRFIListItem.rfi')} {number}
            {versionnumber ? `.${versionnumber}` : null}
          </div>
          {rfiVersionList.size > 1 && (
            <div className="row grow-1 ml2">
              <RfiVersionListDropdown
                rfis={rfiVersionList}
                currentRfiId={rfi.id}
                onRfiSelection={(rfiId) => {
                  const rfi = getRFI(rfiId);
                  onRFIListRowClick(rfi, rfi.postedmarkups.first());
                }}
              />
            </div>
          )}
          <div
            className="row items-center font-size-5"
            style={{
              color: pastDueDate ? 'var(--color-error)' : '#4d555c',
            }}
          >
            <div className="mr1 wrem-1p5 hrem-1p5 flex-center">
              <Calendar width="16px" height="16px" />
            </div>
            {hasDueDate
              ? dateFormatter?.format(new Date(duedate * 1000))
              : translate('rfiPage:noDueDate')}
          </div>
        </div>
        <div className="column pt3">
          <div className="font-size-7 medium mb2">{title}</div>
          <div
            className="row justify-start items-center"
            style={{ color: '#4d555c' }}
          >
            <Profile width="18px" height="18px" />
            <div className="ml1 font-size-5 truncate">{assigneeList}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFIListRow;
